import type {
  CaptureFailureCode,
  CaptureUpload,
} from 'ts-components/PiChat/protocol';
import {
  defaultWarn,
  presignedPut,
  presignedPutBeforeExpiry,
  resolveUploadUrls,
  type Warn,
} from 'ts-components/PiChat/presignedPut';

const MAX_FAILURE_MESSAGE_CHARS = 300;

export type CaptureOutcome =
  | { ok: true; blob: Blob }
  | { ok: false; code: CaptureFailureCode; message?: string };

export type CaptureUploadStatus =
  | 'png_uploaded'
  | 'failure_uploaded'
  | 'expired'
  | 'failed';

interface ResolvedTarget {
  url: string;
  headers: Record<string, string>;
}

export type CaptureFrameType = 'block.capture' | 'ui.capture';

export interface ResolvedCaptureUpload {
  label: CaptureFrameType;
  png: ResolvedTarget;
  failure: ResolvedTarget;
  expiresAt: number;
}

export const resolveCaptureUpload = (
  { png, failure, expiresAt }: CaptureUpload,
  wsUrl: string,
  label: CaptureFrameType,
  warn: Warn = defaultWarn
): ResolvedCaptureUpload | null => {
  const urls = resolveUploadUrls([png.url, failure.url], wsUrl, label, warn);
  if (!urls) return null;
  const [pngUrl, failureUrl] = urls;
  return {
    label,
    png: { url: pngUrl, headers: png.headers },
    failure: { url: failureUrl, headers: failure.headers },
    expiresAt,
  };
};

export interface SendCaptureOutcomeDeps {
  fetch?: typeof fetch;
  warn?: Warn;
}

export const sendCaptureOutcome = (
  upload: ResolvedCaptureUpload,
  outcome: CaptureOutcome,
  {
    fetch: fetchImpl = (...args) => globalThis.fetch(...args),
    warn = defaultWarn,
  }: SendCaptureOutcomeDeps = {}
): Promise<CaptureUploadStatus> =>
  presignedPutBeforeExpiry(
    upload.expiresAt,
    `${upload.label} not answered: its upload URLs have expired`,
    warn,
    async (signal) => {
      const put = (
        target: ResolvedTarget,
        body: Blob,
        what: 'PNG' | 'failure'
      ) =>
        presignedPut(
          target,
          body,
          `${upload.label} ${what}`,
          signal,
          fetchImpl,
          warn
        );

      const putFailure = async (
        code: CaptureFailureCode,
        message: string | undefined
      ): Promise<CaptureUploadStatus> => {
        const body = JSON.stringify({
          code,
          message: message?.slice(0, MAX_FAILURE_MESSAGE_CHARS),
        });
        const result = await put(upload.failure, new Blob([body]), 'failure');
        if (result === 'stored') return 'failure_uploaded';
        return result === 'expired' ? 'expired' : 'failed';
      };

      if (!outcome.ok) return putFailure(outcome.code, outcome.message);
      const result = await put(upload.png, new Blob([outcome.blob]), 'PNG');
      if (result === 'stored') return 'png_uploaded';
      if (result === 'expired') return 'expired';
      return putFailure('capture_failed', 'Uploading the PNG failed');
    }
  );
